import { useState, useEffect, useCallback } from "react";
import { Link as InertiaLink, router } from "@inertiajs/react";
import {
  Card,
  CardBody,
  CardHeader,
  Button,
  Chip,
  Link,
  Input,
  Select,
  SelectItem,
  Pagination,
  Tooltip,
} from "@heroui/react";
import Layout from "../Layouts/Layout";
import { subscribeToRunUpdates } from "../../channels/run_channel";

export default function Index({ runs, pagination, filters, stats }) {
  const [localRuns, setLocalRuns] = useState(runs);
  const [searchQuery, setSearchQuery] = useState(filters?.search || "");
  const [filterStatus, setFilterStatus] = useState(filters?.status || "all");
  const [sortBy, setSortBy] = useState(filters?.sort_by || "recent");

  useEffect(() => {
    const subscription = subscribeToRunUpdates((data) => {
      setLocalRuns((prevRuns) => {
        if (data.action === "created" && data.run) {
          const exists = prevRuns.some((run) => run.id === data.run.id);
          if (exists) return prevRuns;
          return [data.run, ...prevRuns];
        }

        const runExists = prevRuns.some((run) => run.id === data.run_id);
        if (!runExists) return prevRuns;

        return prevRuns.map((run) =>
          run.id === data.run_id
            ? {
                ...run,
                status: data.status,
                output: data.output || run.output,
                error_message: data.error || run.error_message,
                input_tokens: data.tokens?.input || run.input_tokens,
                output_tokens: data.tokens?.output || run.output_tokens,
              }
            : run
        );
      });
    });

    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    setLocalRuns(runs);
  }, [runs]);

  // Update URL parameters when filters change
  const updateFilters = useCallback((newFilters) => {
    router.get('/runs', newFilters, {
      preserveState: true,
      preserveScroll: true,
      only: ['runs', 'pagination', 'filters']
    });
  }, []);

  // Handle search submit
  const handleSearch = useCallback(() => {
    updateFilters({
      search: searchQuery,
      status: filterStatus,
      sort_by: sortBy,
      page: 1
    });
  }, [searchQuery, filterStatus, sortBy, updateFilters]);

  // Handle status filter change
  const handleStatusChange = useCallback((newStatus) => {
    setFilterStatus(newStatus);
    updateFilters({
      search: searchQuery,
      status: newStatus,
      sort_by: sortBy,
      page: 1
    });
  }, [searchQuery, sortBy, updateFilters]);

  // Handle sort change
  const handleSortChange = useCallback((newSortBy) => {
    setSortBy(newSortBy);
    updateFilters({
      search: searchQuery,
      status: filterStatus,
      sort_by: newSortBy,
      page: 1
    });
  }, [searchQuery, filterStatus, updateFilters]);

  const getStatusColor = (status) => {
    switch (status) {
      case "completed":
        return "success";
      case "failed":
        return "danger";
      case "processing":
        return "warning";
      default:
        return "default";
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;

    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
  };

  return (
    <Layout>
      <div className="flex flex-col gap-6 mb-8">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
              Runs
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mt-1">
              Monitor and analyze your task executions
            </p>
          </div>
          <Button
            as={InertiaLink}
            href="/tasks"
            variant="flat"
            color="warning"
            startContent={
              <svg
                className="w-4 h-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M12 4v16m8-8H4"
                />
              </svg>
            }
          >
            New run
          </Button>
        </div>

        {stats && (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
            <div className="bg-white dark:bg-gray-800/50 rounded-xl p-4 border border-gray-200/50 dark:border-gray-700/50">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                Total runs
              </p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                {stats.total}
              </p>
            </div>
            <div className="bg-white dark:bg-gray-800/50 rounded-xl p-4 border border-gray-200/50 dark:border-gray-700/50">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                Success rate
              </p>
              <p className="text-2xl font-bold text-green-600 dark:text-green-400 mt-1">
                {stats.success_rate}%
              </p>
            </div>
            <div className="bg-white dark:bg-gray-800/50 rounded-xl p-4 border border-gray-200/50 dark:border-gray-700/50">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                Completed
              </p>
              <p className="text-2xl font-bold text-green-600 dark:text-green-400 mt-1">
                {stats.completed}
              </p>
            </div>
            <div className="bg-white dark:bg-gray-800/50 rounded-xl p-4 border border-gray-200/50 dark:border-gray-700/50">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                Failed
              </p>
              <p className="text-2xl font-bold text-red-600 dark:text-red-400 mt-1">
                {stats.failed}
              </p>
            </div>
            <div className="bg-white dark:bg-gray-800/50 rounded-xl p-4 border border-gray-200/50 dark:border-gray-700/50">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                Processing
              </p>
              <p className="text-2xl font-bold text-amber-600 dark:text-amber-400 mt-1">
                {stats.processing}
              </p>
            </div>
            <div className="bg-white dark:bg-gray-800/50 rounded-xl p-4 border border-gray-200/50 dark:border-gray-700/50">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                Total tokens
              </p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                {stats.total_tokens.toLocaleString()}
              </p>
            </div>
          </div>
        )}

        {(localRuns.length > 0 || searchQuery || filterStatus !== "all") && (
          <div className="flex flex-col sm:flex-row gap-4">
            <Input
              placeholder="Search by task or input..."
              value={searchQuery}
              onValueChange={setSearchQuery}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleSearch();
                }
              }}
              startContent={
                <svg
                  className="w-4 h-4 text-gray-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
              }
              endContent={
                searchQuery && (
                  <Button
                    isIconOnly
                    size="sm"
                    variant="light"
                    onPress={handleSearch}
                    className="min-w-unit-6 w-6 h-6"
                  >
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={2}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                      />
                    </svg>
                  </Button>
                )
              }
              classNames={{
                base: "max-w-xs",
                inputWrapper:
                  "bg-white dark:bg-gray-800/50 border border-gray-200/50 dark:border-gray-700/50",
              }}
              isClearable
              onClear={() => {
                setSearchQuery("");
                updateFilters({
                  search: "",
                  status: filterStatus,
                  sort_by: sortBy,
                  page: 1
                });
              }}
            />

            <Select
              placeholder="Status"
              selectedKeys={[filterStatus]}
              onSelectionChange={(keys) =>
                handleStatusChange(Array.from(keys)[0])
              }
              classNames={{
                base: "max-w-[150px]",
                trigger:
                  "bg-white dark:bg-gray-800/50 border border-gray-200/50 dark:border-gray-700/50",
              }}
            >
              <SelectItem key="all">All status</SelectItem>
              <SelectItem key="completed">Completed</SelectItem>
              <SelectItem key="failed">Failed</SelectItem>
              <SelectItem key="processing">Processing</SelectItem>
              <SelectItem key="pending">Pending</SelectItem>
            </Select>

            <Select
              placeholder="Sort"
              selectedKeys={[sortBy]}
              onSelectionChange={(keys) => handleSortChange(Array.from(keys)[0])}
              classNames={{
                base: "max-w-[150px]",
                trigger:
                  "bg-white dark:bg-gray-800/50 border border-gray-200/50 dark:border-gray-700/50",
              }}
            >
              <SelectItem key="recent">Most recent</SelectItem>
              <SelectItem key="oldest">Oldest first</SelectItem>
            </Select>

            {(searchQuery || filterStatus !== "all" || sortBy !== "recent") && (
              <Button
                variant="flat"
                color="default"
                size="md"
                onPress={() => {
                  setSearchQuery("");
                  setFilterStatus("all");
                  setSortBy("recent");
                  updateFilters({
                    search: "",
                    status: "all",
                    sort_by: "recent",
                    page: 1
                  });
                }}
                startContent={
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                }
              >
                Clear
              </Button>
            )}
          </div>
        )}
      </div>

      {localRuns.length === 0 ? (
        <Card className="bg-white/80 dark:bg-gray-800/50 backdrop-blur-sm border border-gray-200/50 dark:border-gray-700/50">
          <CardBody className="text-center py-16">
            <div className="w-16 h-16 mx-auto mb-4 bg-amber-100 dark:bg-amber-900/30 rounded-full flex items-center justify-center">
              <svg
                className="w-8 h-8 text-amber-600 dark:text-amber-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M13 10V3L4 14h7v7l9-11h-7z"
                />
              </svg>
            </div>
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
              No runs yet
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-6 max-w-md mx-auto">
              Execute your first task to see run history, performance metrics,
              and results here.
            </p>
            <Button
              as={InertiaLink}
              href="/tasks"
              className="bg-gradient-to-r from-amber-500 to-orange-600 text-white font-semibold"
              size="lg"
            >
              Go to Tasks
            </Button>
          </CardBody>
        </Card>
      ) : localRuns.length === 0 && (searchQuery || filterStatus !== "all") ? (
        <Card className="bg-white/80 dark:bg-gray-800/50 backdrop-blur-sm border border-gray-200/50 dark:border-gray-700/50">
          <CardBody className="text-center py-12">
            <svg
              className="w-12 h-12 mx-auto mb-4 text-gray-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={1.5}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
              No matching runs
            </h3>
            <p className="text-gray-600 dark:text-gray-400">
              Try adjusting your search or filter criteria
            </p>
            <Button
              variant="light"
              color="warning"
              className="mt-4"
              onPress={() => {
                setSearchQuery("");
                setFilterStatus("all");
                setSortBy("recent");
                updateFilters({
                  search: "",
                  status: "all",
                  sort_by: "recent",
                  page: 1
                });
              }}
            >
              Clear filters
            </Button>
          </CardBody>
        </Card>
      ) : (
        <>
          <div className="space-y-4">
            {localRuns.map((run) => (
              <Card
                key={run.id}
                className="bg-white/80 dark:bg-gray-800/50 backdrop-blur-sm border border-gray-200/50 dark:border-gray-700/50 hover:shadow-md transition-shadow"
              >
                <CardHeader className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 pb-2">
                  <div className="flex items-center gap-3">
                    <Link
                      as={InertiaLink}
                      href={`/tasks/${run.task.id}`}
                      className="text-lg font-semibold text-gray-900 dark:text-white hover:text-amber-600 dark:hover:text-amber-400 transition-colors"
                    >
                      {run.task.name}
                    </Link>
                    <Chip
                      color={getStatusColor(run.status)}
                      variant="flat"
                      size="sm"
                      startContent={
                        run.status === "processing" ? (
                          <span className="w-2 h-2 bg-current rounded-full animate-pulse" />
                        ) : null
                      }
                    >
                      {run.status}
                    </Chip>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={2}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                    <Tooltip content={new Date(run.created_at).toLocaleString()}>
                      <span>{formatDate(run.created_at)}</span>
                    </Tooltip>
                  </div>
                </CardHeader>

                <CardBody className="pt-0">
                  <div className="space-y-4">
                    <div className="bg-gray-50 dark:bg-gray-900/50 rounded-lg p-3">
                      <div className="flex justify-between items-center mb-2">
                        <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Input
                        </p>
                        <Tooltip content="Copy input">
                          <Button
                            isIconOnly
                            size="sm"
                            variant="light"
                            onPress={() => copyToClipboard(run.input)}
                          >
                            <svg
                              className="w-3.5 h-3.5"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                              strokeWidth={2}
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                              />
                            </svg>
                          </Button>
                        </Tooltip>
                      </div>
                      <p className="text-sm text-gray-700 dark:text-gray-300 line-clamp-2 font-mono">
                        {run.input}
                      </p>
                    </div>

                    {run.status === "completed" && run.output && (
                      <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-3">
                        <div className="flex justify-between items-center mb-2">
                          <p className="text-xs font-semibold text-green-600 dark:text-green-400 uppercase tracking-wider">
                            Output
                          </p>
                          <Tooltip content="Copy output">
                            <Button
                              isIconOnly
                              size="sm"
                              variant="light"
                              onPress={() => copyToClipboard(run.output)}
                            >
                              <svg
                                className="w-3.5 h-3.5"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                                strokeWidth={2}
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                                />
                              </svg>
                            </Button>
                          </Tooltip>
                        </div>
                        <p className="text-sm text-gray-700 dark:text-gray-300 line-clamp-3 font-mono">
                          {run.output}
                        </p>
                      </div>
                    )}

                    {run.status === "failed" && run.error_message && (
                      <div className="bg-red-50 dark:bg-red-900/20 rounded-lg p-3">
                        <p className="text-xs font-semibold text-red-600 dark:text-red-400 uppercase tracking-wider mb-2">
                          Error
                        </p>
                        <p className="text-sm text-red-700 dark:text-red-300 font-mono">
                          {run.error_message}
                        </p>
                      </div>
                    )}
                  </div>

                  <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-200/50 dark:border-gray-700/50">
                    <div className="flex items-center gap-4">
                      {run.input_tokens && (
                        <Tooltip
                          content={`Input: ${run.input_tokens.toLocaleString()} | Output: ${run.output_tokens.toLocaleString()}`}
                        >
                          <div className="flex items-center gap-1.5 text-sm text-gray-500 dark:text-gray-400">
                            <svg
                              className="w-4 h-4"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                              strokeWidth={2}
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z"
                              />
                            </svg>
                            <span>
                              {(
                                run.input_tokens + run.output_tokens
                              ).toLocaleString()}{" "}
                              tokens
                            </span>
                          </div>
                        </Tooltip>
                      )}
                    </div>
                    <Button
                      as={InertiaLink}
                      href={`/runs/${run.id}`}
                      variant="light"
                      color="warning"
                      size="sm"
                      endContent={
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          strokeWidth={2}
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M9 5l7 7-7 7"
                          />
                        </svg>
                      }
                    >
                      View Details
                    </Button>
                  </div>
                </CardBody>
              </Card>
            ))}
          </div>

          {pagination.total_pages > 1 && (
            <div className="mt-8 flex justify-center">
              <Pagination
                total={pagination.total_pages}
                page={pagination.current_page}
                onChange={(page) => {
                  router.get('/runs', {
                    search: searchQuery,
                    status: filterStatus,
                    sort_by: sortBy,
                    page: page
                  }, {
                    preserveState: true,
                    preserveScroll: false
                  });
                }}
                showControls
                classNames={{
                  cursor:
                    "bg-gradient-to-r from-amber-500 to-orange-600 text-white font-semibold shadow-lg",
                }}
              />
            </div>
          )}
        </>
      )}
    </Layout>
  );
}
